import { memo, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Bookmark, ExternalLink, MessageSquare, Reply, Send } from "lucide-react";
import type { CompiledRegion, ResolvedGuideItem } from "../../shared/codemap";
import type { ReviewAnnotation } from "../../shared/guide";
import { draftAnchorSpan, type DraftAnchor, type DraftCommentKind } from "../lib/db";
import type { FixtureFile } from "../lib/fixtures";
import type { HostedConversationComment, HostedReviewThread } from "../lib/hosted";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  buildSplitRows,
  collapseUnchangedRows,
  type CollapsedDiffRows,
  type DiffSide,
  type UnifiedDiffRow,
} from "../lib/unified-diff";
import { ReviewCommentBody } from "./ReviewCommentBody";

interface DiffViewerProps {
  /** Anchor open in the composer, kept highlighted while the reviewer types. */
  activeAnchor?: DraftAnchor;
  annotations: ReviewAnnotation[];
  comments: DiffLineComment[];
  bookmarks: DiffLineBookmark[];
  codeCommentMode: CodeCommentMode;
  file: FixtureFile;
  path: string;
  rows: UnifiedDiffRow[];
  focus?: ResolvedGuideItem;
  regions: CompiledRegion[];
  onSelectComment: (anchor: DraftAnchor) => void;
  onToggleBookmark: (anchor: DraftAnchor, lineText: string) => void;
  revealLine?: DiffLineReveal;
  onLineRevealed?: (token: number) => void;
  revealToken: number;
  reviewThreads: HostedReviewThread[];
  codePaths: string[];
  onReplyThread?: (commentId: number, body: string) => Promise<HostedConversationComment>;
  onSelectLocation: (path: string, line?: number, side?: DiffSide) => void;
  wrapCode: boolean;
  splitView: boolean;
}

export type CodeCommentMode = "show" | "extract";

export interface DiffLineComment {
  itemId: string;
  side: DiffSide;
  line: number;
  /** First line when the comment spans a range; absent for a single line. */
  startLine?: number;
  kind: DraftCommentKind;
  body: string;
}

export interface DiffLineBookmark {
  side: DiffSide;
  line: number;
}

export interface DiffLineReveal {
  side: DiffSide;
  line: number;
  token: number;
}

const commentKindLabels: Record<DraftCommentKind, string> = {
  note: "Note",
  question: "Question",
  tip: "Tip",
  change: "Change",
};

type SyntaxKind = "comment" | "keyword" | "literal" | "number" | "property" | "string" | "type";

export interface SyntaxToken {
  kind?: SyntaxKind;
  text: string;
}

interface SyntaxState {
  blockComment: boolean;
  quote?: "\"" | "'" | "`";
}

const keywords = new Set([
  "abstract", "as", "async", "await", "break", "case", "catch", "class", "const", "continue",
  "default", "delete", "do", "else", "enum", "export", "extends", "finally", "for", "foreach",
  "from", "function", "get", "if", "implements", "import", "in", "interface", "internal", "is", "let",
  "namespace", "new", "of", "override", "private", "protected", "public", "readonly", "return", "select",
  "set", "static", "struct", "switch", "throw", "try", "type", "typeof", "using", "var", "void", "when",
  "where", "while", "with", "yield",
]);

const literals = new Set(["false", "null", "true", "undefined"]);

function pushToken(tokens: SyntaxToken[], text: string, kind?: SyntaxKind) {
  if (!text) return;
  const last = tokens.at(-1);
  if (last && last.kind === kind) last.text += text;
  else tokens.push({ kind, text });
}

function commentAt(text: string, index: number, language: string): "block" | "line" | undefined {
  if (text.startsWith("/*", index)) return "block";
  if (language === "sql" && text.startsWith("--", index)) return "line";
  if (["terraform", "yaml"].includes(language) && text[index] === "#" && (index === 0 || /\s/.test(text[index - 1]!))) return "line";
  if (text.startsWith("//", index)) return "line";
  return undefined;
}

function tokenizeCodeLine(text: string, language: string, initialState: SyntaxState): { state: SyntaxState; tokens: SyntaxToken[] } {
  const tokens: SyntaxToken[] = [];
  const state = { ...initialState };
  let cursor = 0;
  let plainStart = 0;
  const flushPlain = () => {
    pushToken(tokens, text.slice(plainStart, cursor));
    plainStart = cursor;
  };

  while (cursor < text.length) {
    if (state.blockComment) {
      flushPlain();
      const end = text.indexOf("*/", cursor);
      if (end < 0) {
        pushToken(tokens, text.slice(cursor), "comment");
        cursor = text.length;
      } else {
        pushToken(tokens, text.slice(cursor, end + 2), "comment");
        cursor = end + 2;
        state.blockComment = false;
      }
      plainStart = cursor;
      continue;
    }

    if (state.quote) {
      flushPlain();
      const start = cursor;
      while (cursor < text.length) {
        if (text[cursor] === "\\") cursor = Math.min(text.length, cursor + 2);
        else if (text[cursor] === state.quote) {
          cursor += 1;
          state.quote = undefined;
          break;
        } else cursor += 1;
      }
      pushToken(tokens, text.slice(start, cursor), "string");
      plainStart = cursor;
      continue;
    }

    const comment = commentAt(text, cursor, language);
    if (comment) {
      flushPlain();
      if (comment === "line") {
        pushToken(tokens, text.slice(cursor), "comment");
        cursor = text.length;
      } else {
        const end = text.indexOf("*/", cursor + 2);
        if (end < 0) {
          pushToken(tokens, text.slice(cursor), "comment");
          state.blockComment = true;
          cursor = text.length;
        } else {
          pushToken(tokens, text.slice(cursor, end + 2), "comment");
          cursor = end + 2;
        }
      }
      plainStart = cursor;
      continue;
    }

    const character = text[cursor]!;
    if (character === '"' || character === "'" || character === "`") {
      flushPlain();
      const quote = character;
      const start = cursor;
      cursor += 1;
      let closed = false;
      while (cursor < text.length) {
        if (text[cursor] === "\\") cursor = Math.min(text.length, cursor + 2);
        else if (text[cursor] === quote) {
          cursor += 1;
          closed = true;
          break;
        } else cursor += 1;
      }
      pushToken(tokens, text.slice(start, cursor), "string");
      if (!closed) state.quote = quote;
      plainStart = cursor;
      continue;
    }

    if (/\d/.test(character) && !/[\w$]/.test(text[cursor - 1] ?? "")) {
      flushPlain();
      const start = cursor;
      while (cursor < text.length && /[\d._xXa-fA-F]/.test(text[cursor]!)) cursor += 1;
      pushToken(tokens, text.slice(start, cursor), "number");
      plainStart = cursor;
      continue;
    }

    if (/[A-Za-z_$]/.test(character)) {
      flushPlain();
      const start = cursor;
      cursor += 1;
      while (cursor < text.length && /[\w$-]/.test(text[cursor]!)) cursor += 1;
      const word = text.slice(start, cursor);
      const rest = text.slice(cursor);
      const kind = keywords.has(word)
        ? "keyword"
        : literals.has(word)
          ? "literal"
          : /^[A-Z]/.test(word)
            ? "type"
            : /^\s*:/.test(rest)
              ? "property"
              : undefined;
      pushToken(tokens, word, kind);
      plainStart = cursor;
      continue;
    }

    cursor += 1;
  }

  flushPlain();
  return { state, tokens };
}

export function tokenizeCodeLines(lines: string[], language: string): SyntaxToken[][] {
  let state: SyntaxState = { blockComment: false };
  return lines.map((line) => {
    const result = tokenizeCodeLine(line, language, state);
    state = result.state;
    return result.tokens;
  });
}

function syntaxByDiffRow(rows: UnifiedDiffRow[], language: string): Map<string, SyntaxToken[]> {
  let leftState: SyntaxState = { blockComment: false };
  let rightState: SyntaxState = { blockComment: false };
  const result = new Map<string, SyntaxToken[]>();
  for (const row of rows) {
    if (row.kind === "delete") {
      const tokenized = tokenizeCodeLine(row.text, language, leftState);
      leftState = tokenized.state;
      result.set(row.id, tokenized.tokens);
    } else if (row.kind === "add") {
      const tokenized = tokenizeCodeLine(row.text, language, rightState);
      rightState = tokenized.state;
      result.set(row.id, tokenized.tokens);
    } else {
      if (leftState.blockComment === rightState.blockComment && leftState.quote === rightState.quote) {
        const tokenized = tokenizeCodeLine(row.text, language, rightState);
        leftState = { ...tokenized.state };
        rightState = tokenized.state;
        result.set(row.id, tokenized.tokens);
        continue;
      }
      const left = tokenizeCodeLine(row.text, language, leftState);
      const right = tokenizeCodeLine(row.text, language, rightState);
      leftState = left.state;
      rightState = right.state;
      result.set(row.id, right.tokens);
    }
  }
  return result;
}

const CodeLine = memo(function CodeLine({ commentMode, tokens }: { commentMode: CodeCommentMode; tokens: SyntaxToken[] }) {
  return (
    <code>
      {tokens.map((token, index) => {
        if (token.kind === "comment" && commentMode === "extract") {
          const comment = token.text.trim();
          return (
            <Tooltip key={`${index}:${token.text}`}>
              <TooltipTrigger
                render={<span aria-label={`Code comment: ${comment || "empty comment"}`} className="syntax-comment-extracted" role="note" tabIndex={0} />}
              >
                <MessageSquare aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent className="code-comment-tooltip" side="top" sideOffset={5}><code>{comment}</code></TooltipContent>
            </Tooltip>
          );
        }
        return <span className={token.kind ? `syntax-${token.kind}` : undefined} key={`${index}:${token.text}`}>{token.text}</span>;
      })}
    </code>
  );
});

function commentTarget(row: UnifiedDiffRow): { side: DiffSide; line: number } | undefined {
  if (row.kind === "delete" && row.oldLine !== undefined) return { side: "LEFT", line: row.oldLine };
  if (row.newLine !== undefined) return { side: "RIGHT", line: row.newLine };
  if (row.oldLine !== undefined) return { side: "LEFT", line: row.oldLine };
  return undefined;
}

function matchesRange(row: UnifiedDiffRow, range: { side: DiffSide; startLine: number; endLine: number }) {
  const line = range.side === "LEFT" ? row.oldLine : row.newLine;
  return line !== undefined && line >= range.startLine && line <= range.endLine;
}

function endsRange(row: UnifiedDiffRow, range: { side: DiffSide; endLine: number }) {
  return (range.side === "LEFT" ? row.oldLine : row.newLine) === range.endLine;
}

function lineLabel(side: DiffSide, line: number) {
  return `${side === "LEFT" ? "removed" : "current"} line ${line}`;
}

interface PendingRange {
  side: DiffSide;
  /** Line the drag started on; the drag may run in either direction from here. */
  anchorLine: number;
  focusLine: number;
}

function pendingSpan(pending: PendingRange) {
  return {
    startLine: Math.min(pending.anchorLine, pending.focusLine),
    endLine: Math.max(pending.anchorLine, pending.focusLine),
  };
}

/**
 * Resolve the diff line sitting under the pointer. Ranges cannot cross sides on
 * GitHub, so a drag that wanders onto the other side of the diff is ignored
 * rather than silently retargeted.
 */
function lineUnderPointer(clientX: number, clientY: number, side: DiffSide): number | undefined {
  const row = document.elementFromPoint(clientX, clientY)?.closest<HTMLElement>(".diff-row");
  const value = Number(row?.dataset[side === "LEFT" ? "leftLine" : "rightLine"]);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

function lineRangeLabel(side: DiffSide, startLine: number, endLine: number) {
  const prefix = side === "LEFT" ? "Removed" : "Current";
  return startLine === endLine ? `${prefix} line ${startLine}` : `${prefix} lines ${startLine}–${endLine}`;
}

function activityAge(createdAt: string) {
  const elapsed = Math.max(0, Date.now() - Date.parse(createdAt));
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return days < 14 ? `${days}d` : new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).format(new Date(createdAt));
}

function InlineReviewThread({
  codePaths,
  onReplyThread,
  onSelectLocation,
  thread,
}: {
  codePaths: string[];
  onReplyThread?: (commentId: number, body: string) => Promise<HostedConversationComment>;
  onSelectLocation: (path: string, line?: number, side?: DiffSide) => void;
  thread: HostedReviewThread;
}) {
  const [localReplies, setLocalReplies] = useState<HostedConversationComment[]>([]);
  const [replying, setReplying] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replyError, setReplyError] = useState<string>();
  const [sending, setSending] = useState(false);
  const comments = [...thread.comments, ...localReplies];
  const first = comments[0];
  const latest = comments.at(-1);
  if (!first || !latest) return null;
  const canReply = Boolean(onReplyThread && !thread.resolved && !thread.outdated);
  const sendReply = async () => {
    const body = replyBody.trim();
    if (!body || !onReplyThread || sending) return;
    setSending(true);
    setReplyError(undefined);
    try {
      const reply = await onReplyThread(first.id, body);
      setLocalReplies((current) => [...current, reply]);
      setReplyBody("");
      setReplying(false);
    } catch (reason) {
      setReplyError(reason instanceof Error ? reason.message : "Could not send the reply.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className={`diff-review-thread${thread.resolved ? " resolved" : ""}${thread.outdated ? " outdated" : ""}`}>
      <header>
        <span aria-hidden="true" className="conversation-avatar">{first.author.slice(0, 1).toUpperCase()}</span>
        <strong>{comments.length === 1 ? `@${first.author}` : `${comments.length} comments`}</strong>
        <time dateTime={latest.createdAt}>{activityAge(latest.createdAt)}</time>
        <em>{thread.resolved ? "Resolved" : thread.outdated ? "Outdated" : "Open"}</em>
      </header>
      <div className="thread-replies diff-thread-comments">
        {comments.map((comment) => (
          <div key={comment.url}>
            <header><strong>@{comment.author}</strong><time dateTime={comment.createdAt}>{activityAge(comment.createdAt)}</time></header>
            <p><ReviewCommentBody body={comment.body} codePaths={codePaths} onSelectLocation={(path, line) => onSelectLocation(path, line)} /></p>
          </div>
        ))}
      </div>
      <footer>
        <button onClick={() => onSelectLocation(thread.path, thread.line, thread.side)} title={thread.path} type="button">{thread.path.split("/").at(-1)}{thread.line ? `:${thread.line}` : ""}</button>
        {canReply && <button onClick={() => { setReplying((open) => !open); setReplyError(undefined); }} type="button"><Reply aria-hidden="true" />Reply</button>}
        <a aria-label="Open thread on GitHub" href={latest.url} rel="noreferrer" target="_blank"><ExternalLink aria-hidden="true" /></a>
      </footer>
      {replying && (
        <div className="thread-reply-composer diff-thread-reply">
          <textarea aria-label={`Reply to @${first.author}`} autoFocus onChange={(event) => setReplyBody(event.target.value)} placeholder="Reply to this thread…" rows={2} value={replyBody} />
          {replyError && <p role="alert">{replyError}</p>}
          <footer><button onClick={() => setReplying(false)} type="button">Cancel</button><button disabled={!replyBody.trim() || sending} onClick={() => void sendReply()} type="button"><Send aria-hidden="true" />{sending ? "Sending…" : "Send reply"}</button></footer>
        </div>
      )}
    </section>
  );
}

export function DiffViewer({
  activeAnchor,
  annotations,
  bookmarks,
  codeCommentMode,
  comments,
  file,
  path,
  focus,
  onSelectComment,
  onToggleBookmark,
  regions,
  revealLine,
  onLineRevealed,
  revealToken,
  reviewThreads,
  codePaths,
  onReplyThread,
  onSelectLocation,
  rows,
  wrapCode,
  splitView,
}: DiffViewerProps) {
  const fileName = path.split("/").at(-1) ?? path;
  const hostRef = useRef<HTMLDivElement>(null);
  const [expandedContext, setExpandedContext] = useState<Set<string>>(() => new Set());
  const [pendingRange, setPendingRange] = useState<PendingRange>();
  const pendingRef = useRef<PendingRange>(undefined);
  // Line of the last plain press, so Shift-click can extend from it the way the
  // guide author's line picker does.
  const lastPressRef = useRef<{ side: DiffSide; line: number }>(undefined);
  // A range that ends on the row it started from still emits a click, which
  // would reopen the same anchor as a single line. Single-line selections are
  // left to that click, so only ranges suppress it.
  const suppressClickRef = useRef(false);

  const beginRangeSelection = (event: ReactPointerEvent<HTMLElement>, side: DiffSide, line: number) => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey) return;
    const previous = lastPressRef.current;
    const anchorLine = event.shiftKey && previous?.side === side ? previous.line : line;
    if (!event.shiftKey) lastPressRef.current = { side, line };
    const started: PendingRange = { side, anchorLine, focusLine: line };
    pendingRef.current = started;
    setPendingRange(started);

    const extendTo = (nextLine: number) => {
      const current = pendingRef.current;
      if (!current || current.focusLine === nextLine) return;
      const next = { ...current, focusLine: nextLine };
      pendingRef.current = next;
      setPendingRange(next);
    };

    const onPointerMove = (moved: PointerEvent) => {
      const nextLine = lineUnderPointer(moved.clientX, moved.clientY, side);
      if (nextLine !== undefined) extendTo(nextLine);
    };

    const finish = (commit: boolean) => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
      window.removeEventListener("keydown", onKeyDown, true);
      const current = pendingRef.current;
      pendingRef.current = undefined;
      setPendingRange(undefined);
      if (!commit || !current) return;
      const { startLine, endLine } = pendingSpan(current);
      if (startLine === endLine) return;
      lastPressRef.current = { side, line: endLine };
      // The click lands in the same task as this pointerup, so releasing the
      // guard on the next tick keeps it from swallowing an unrelated later
      // click when the drag ends away from the button it started on.
      suppressClickRef.current = true;
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
      onSelectComment({ kind: "line", path, side, line: endLine, startLine });
    };

    const onPointerUp = () => finish(true);
    const onPointerCancel = () => finish(false);
    const onKeyDown = (pressed: KeyboardEvent) => {
      if (pressed.key === "Escape") finish(false);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    window.addEventListener("keydown", onKeyDown, true);
  };
  const threadRanges = useMemo(() => reviewThreads.flatMap((thread) => thread.line ? [{
    side: thread.side ?? "RIGHT" as const,
    startLine: thread.startLine ?? thread.line,
    endLine: thread.line,
  }] : []), [reviewThreads]);
  const activeSelection = useMemo(
    () => activeAnchor ? { side: activeAnchor.side, ...draftAnchorSpan(activeAnchor) } : undefined,
    [activeAnchor],
  );
  const pendingSelection = useMemo(
    () => pendingRange ? { side: pendingRange.side, ...pendingSpan(pendingRange) } : undefined,
    [pendingRange],
  );
  const displayRows = useMemo(
    // Guided focus may change while the reviewer scrolls. Folding is explicit
    // UI state and must not be recalculated from that moving cursor, otherwise
    // the document expands and shifts underneath the reviewer.
    () => collapseUnchangedRows(rows, { expanded: expandedContext, visibleRanges: threadRanges }),
    [expandedContext, rows, threadRanges],
  );
  const splitRows = useMemo(() => (splitView ? buildSplitRows(displayRows) : undefined), [displayRows, splitView]);
  const syntaxByRow = useMemo(() => syntaxByDiffRow(rows, file.language), [file.language, rows]);
  const emphasisByLine = useMemo(() => {
    const result = new Map<string, CompiledRegion>();
    for (const region of regions) {
      for (const line of region.changedLines) result.set(`${line.side}:${line.line}`, region);
    }
    return result;
  }, [regions]);
  const commentsByLine = useMemo(
    () => new Map(comments.map((comment) => [`${comment.side}:${comment.line}`, comment])),
    [comments],
  );
  const commentRangeKeys = useMemo(() => comments.reduce<Set<string>>((keys, comment) => {
    for (let line = comment.startLine ?? comment.line; line <= comment.line; line += 1) keys.add(`${comment.side}:${line}`);
    return keys;
  }, new Set()), [comments]);
  const bookmarkKeys = useMemo(
    () => new Set(bookmarks.map((bookmark) => `${bookmark.side}:${bookmark.line}`)),
    [bookmarks],
  );
  const threadsByLine = useMemo(() => reviewThreads.reduce<Map<string, HostedReviewThread[]>>((grouped, thread) => {
    if (!thread.line) return grouped;
    const key = `${thread.side ?? "RIGHT"}:${thread.line}`;
    grouped.set(key, [...(grouped.get(key) ?? []), thread]);
    return grouped;
  }, new Map()), [reviewThreads]);
  const threadRangeKeys = useMemo(() => reviewThreads.reduce<Set<string>>((keys, thread) => {
    if (!thread.line) return keys;
    const side = thread.side ?? "RIGHT";
    for (let line = thread.startLine ?? thread.line; line <= thread.line; line += 1) keys.add(`${side}:${line}`);
    return keys;
  }, new Set()), [reviewThreads]);

  useEffect(() => setExpandedContext(new Set()), [path]);

  useEffect(() => {
    if (!focus) return;
    window.requestAnimationFrame(() => {
      const attribute = focus.side === "LEFT" ? "leftLine" : "rightLine";
      const centerLine = (focus.startLine + focus.endLine) / 2;
      const rowsInRange = [...(hostRef.current?.querySelectorAll<HTMLElement>(".diff-row") ?? [])]
        .map((row) => ({ row, line: Number(row.dataset[attribute]) }))
        .filter(({ line }) => Number.isFinite(line) && line >= focus.startLine && line <= focus.endLine)
        .sort((left, right) => {
          const changedDifference = Number(!left.row.matches(".diff-changed")) - Number(!right.row.matches(".diff-changed"));
          return changedDifference || Math.abs(left.line - centerLine) - Math.abs(right.line - centerLine);
        });
      rowsInRange[0]?.row.scrollIntoView({ block: "center" });
    });
  }, [path, revealToken]);

  useEffect(() => {
    if (!revealLine) return;
    const attribute = revealLine.side === "LEFT" ? "leftLine" : "rightLine";
    const frame = window.requestAnimationFrame(() => {
      const row = [...(hostRef.current?.querySelectorAll<HTMLElement>(".diff-row") ?? [])]
        .find((candidate) => Number(candidate.dataset[attribute]) === revealLine.line);
      if (row) {
        row.scrollIntoView({ block: "center" });
        // One-shot: without this, later row changes (files loading, folds expanding) would snap back here.
        onLineRevealed?.(revealLine.token);
        return;
      }
      const folded = displayRows.find((candidate) => {
        if (candidate.kind !== "collapsed") return false;
        const start = revealLine.side === "LEFT" ? candidate.oldStart : candidate.newStart;
        const end = revealLine.side === "LEFT" ? candidate.oldEnd : candidate.newEnd;
        return start !== undefined && end !== undefined && revealLine.line >= start && revealLine.line <= end;
      });
      if (folded?.kind === "collapsed") {
        setExpandedContext((current) => new Set(current).add(folded.id));
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [displayRows, onLineRevealed, revealLine]);

  const renderLineActions = (target: { side: DiffSide; line: number }, rowText: string, comment: DiffLineComment | undefined, bookmarked: boolean) => (
    <span className="diff-line-actions">
      <button
        aria-label={`${comment ? "Edit comment on" : "Comment on"} ${lineLabel(target.side, target.line)} in ${fileName}. Drag to comment on a range of lines.`}
        className={`diff-add-comment${comment ? " has-comment" : ""}`}
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false;
            return;
          }
          onSelectComment({ kind: "line", path, side: target.side, line: target.line });
        }}
        onPointerDown={(event) => beginRangeSelection(event, target.side, target.line)}
        title={`${comment ? "Edit comment on" : "Comment on"} ${lineLabel(target.side, target.line)} · drag for a range`}
        type="button"
      >
        {comment ? <MessageSquare aria-hidden="true" /> : "+"}
      </button>
      <button
        aria-label={`${bookmarked ? "Remove bookmark from" : "Bookmark"} ${lineLabel(target.side, target.line)} in ${fileName}`}
        className={`diff-bookmark-line${bookmarked ? " bookmarked" : ""}`}
        onClick={() => onToggleBookmark({ kind: "line", path, side: target.side, line: target.line }, rowText)}
        title={`${bookmarked ? "Remove bookmark" : "Bookmark"} ${lineLabel(target.side, target.line)}`}
        type="button"
      ><Bookmark aria-hidden="true" /></button>
    </span>
  );

  const renderCollapsed = (row: CollapsedDiffRows) => (
    <button
      className="diff-collapsed-context"
      key={row.id}
      onClick={() => setExpandedContext((current) => new Set(current).add(row.id))}
      title={`Show lines ${row.newStart ?? row.oldStart}–${row.newEnd ?? row.oldEnd}`}
      type="button"
    >
      <span>{row.hiddenLines} unmodified lines</span>
      <small>Expand</small>
    </button>
  );

  // Threads, saved comments and range annotations that follow a rendered line.
  // In split view one visual row can carry both an old- and a new-side line, so
  // this collects across every line the row shows.
  const renderRowExtras = (subrows: UnifiedDiffRow[]) => {
    const keys: string[] = [];
    for (const row of subrows) {
      if (row.oldLine !== undefined) keys.push(`LEFT:${row.oldLine}`);
      if (row.newLine !== undefined) keys.push(`RIGHT:${row.newLine}`);
    }
    const rowThreads = keys.flatMap((key) => threadsByLine.get(key) ?? []);
    const rowComments = keys.flatMap((key) => {
      const comment = commentsByLine.get(key);
      return comment ? [comment] : [];
    });
    const endingAnnotations = annotations.filter(
      (annotation) => subrows.some((row) => endsRange(row, annotation) && matchesRange(row, annotation)),
    );
    if (rowThreads.length === 0 && rowComments.length === 0 && endingAnnotations.length === 0) return null;
    return (
      <>
        {rowThreads.map((thread) => <InlineReviewThread codePaths={codePaths} key={thread.id} onReplyThread={onReplyThread} onSelectLocation={onSelectLocation} thread={thread} />)}
        {rowComments.map((comment) => (
          <button
            className="diff-inline-comment"
            key={`${comment.side}:${comment.line}`}
            onClick={() => {
              onSelectComment({ kind: "line", path, side: comment.side, line: comment.line, ...(comment.startLine === undefined ? {} : { startLine: comment.startLine }) });
            }}
            type="button"
          ><span className={`draft-kind draft-kind-${comment.kind}`}>{commentKindLabels[comment.kind]}</span><p>{comment.body}</p><small>{comment.startLine !== undefined && comment.startLine !== comment.line ? `${comment.startLine}–${comment.line} · Edit` : "Edit"}</small></button>
        ))}
        {endingAnnotations.map((annotation) => (
          <aside className={`diff-range-annotation annotation-${annotation.tone}`} key={annotation.id}>
            <div>
              <strong>{annotation.author.name}</strong>
              <span>{annotation.author.type === "agent" ? "Agent annotation" : "Human annotation"}</span>
              <small>{lineRangeLabel(annotation.side, annotation.startLine, annotation.endLine)}</small>
            </div>
            <p>{annotation.body}</p>
          </aside>
        ))}
      </>
    );
  };

  // An in-flight drag previews over the composer's saved anchor.
  const selection = pendingSelection ?? activeSelection;

  const renderSplitCell = (row: UnifiedDiffRow | undefined, side: DiffSide) => {
    if (!row) return <span aria-hidden="true" className="diff-cell diff-cell-empty" />;
    const line = side === "LEFT" ? row.oldLine : row.newLine;
    const target = line === undefined ? undefined : { side, line };
    const key = target ? `${side}:${line}` : undefined;
    const comment = key ? commentsByLine.get(key) : undefined;
    const commented = Boolean(key && commentRangeKeys.has(key));
    const selecting = Boolean(target && selection && selection.side === side
      && target.line >= selection.startLine && target.line <= selection.endLine);
    const bookmarked = key ? bookmarkKeys.has(key) : false;
    const revealed = Boolean(target && revealLine && side === revealLine.side && target.line === revealLine.line);
    const emphasis = row.kind !== "context" && key ? emphasisByLine.get(key) : undefined;
    const threaded = Boolean(key && threadRangeKeys.has(key));
    const annotated = annotations.some((annotation) => annotation.side === side && matchesRange(row, annotation));
    const emphasisTitle = emphasis
      ? `${emphasis.scrutiny} emphasis · ${emphasis.nature.replaceAll("-", " ")}${emphasis.concerns.length ? ` · ${emphasis.concerns.join(" · ")}` : ""}`
      : undefined;
    return (
      <span
        className={`diff-cell diff-cell-${row.kind}${emphasis ? ` diff-emphasis-${emphasis.scrutiny}` : ""}${commented ? " diff-has-comment" : ""}${selecting ? " diff-line-selected" : ""}${bookmarked ? " diff-bookmarked" : ""}${revealed ? " diff-revealed-line" : ""}${threaded ? " diff-has-review-thread" : ""}`}
        role="cell"
        title={emphasisTitle}
      >
        <span className="diff-cell-rail">
          <span className="diff-comment-gutter">
            {target && renderLineActions(target, row.text, comment, bookmarked)}
            {annotated && <span aria-hidden="true" className="diff-annotation-tick" />}
          </span>
          <span className="diff-line-number">{line}</span>
          <span aria-hidden="true" className="diff-marker">{row.kind === "add" ? "+" : row.kind === "delete" ? "−" : ""}</span>
        </span>
        <span className="diff-code"><CodeLine commentMode={codeCommentMode} tokens={syntaxByRow.get(row.id) ?? [{ text: row.text }]} /></span>
      </span>
    );
  };

  return (
    <div aria-label={`${splitView ? "Split" : "Unified"} diff for ${path}`} className={`diff-viewer light-diff${wrapCode ? " diff-wrap" : ""}${splitView ? " diff-split" : ""}${pendingRange ? " diff-drag-selecting" : ""}`} ref={hostRef}>
      <div className="diff-scroll" role="table">
        {splitRows
          ? splitRows.map((row) => {
            if (row.kind === "collapsed") return renderCollapsed(row);
            const changed = row.left?.kind === "delete" || row.right?.kind === "add";
            const subrows = row.left === row.right ? (row.left ? [row.left] : []) : [row.left, row.right].filter((subrow): subrow is UnifiedDiffRow => Boolean(subrow));
            return (
              <div key={row.id}>
                <div
                  className={`diff-row diff-split-pair${changed ? " diff-changed" : ""}`}
                  data-left-line={row.left?.oldLine}
                  data-right-line={row.right?.newLine}
                  role="row"
                >
                  {renderSplitCell(row.left, "LEFT")}
                  {renderSplitCell(row.right, "RIGHT")}
                </div>
                {renderRowExtras(subrows)}
              </div>
            );
          })
          : displayRows.map((row) => {
            if (row.kind === "collapsed") return renderCollapsed(row);

            const target = commentTarget(row);
            const key = target ? `${target.side}:${target.line}` : undefined;
            const comment = key ? commentsByLine.get(key) : undefined;
            const commented = Boolean(key && commentRangeKeys.has(key));
            const selecting = Boolean(target && selection && selection.side === target.side
              && target.line >= selection.startLine && target.line <= selection.endLine);
            const bookmarked = key ? bookmarkKeys.has(key) : false;
            const revealed = Boolean(target && revealLine && target.side === revealLine.side && target.line === revealLine.line);
            const emphasis = key ? emphasisByLine.get(key) : undefined;
            const rowAnnotations = annotations.filter((annotation) => matchesRange(row, annotation));
            const threaded = Boolean(key && threadRangeKeys.has(key));
            const emphasisTitle = emphasis
              ? `${emphasis.scrutiny} emphasis · ${emphasis.nature.replaceAll("-", " ")}${emphasis.concerns.length ? ` · ${emphasis.concerns.join(" · ")}` : ""}`
              : undefined;
            return (
              <div key={row.id}>
                <div
                  className={`diff-row diff-${row.kind}${row.kind !== "context" ? " diff-changed" : ""}${emphasis ? ` diff-emphasis-${emphasis.scrutiny}` : ""}${commented ? " diff-has-comment" : ""}${selecting ? " diff-line-selected" : ""}${bookmarked ? " diff-bookmarked" : ""}${revealed ? " diff-revealed-line" : ""}${rowAnnotations.length ? " diff-has-annotation" : ""}${threaded ? " diff-has-review-thread" : ""}`}
                  data-left-line={row.oldLine}
                  data-right-line={row.newLine}
                  data-diff-side={row.kind === "delete" ? "left" : row.kind === "add" ? "right" : "both"}
                  role="row"
                  title={emphasisTitle}
                >
                  <span className="diff-line-rail">
                    <span className="diff-comment-gutter" role="cell">
                      {target && renderLineActions(target, row.text, comment, bookmarked)}
                      {rowAnnotations.length > 0 && <span aria-hidden="true" className="diff-annotation-tick" />}
                    </span>
                    <span className="diff-line-number" role="cell">{row.oldLine}</span>
                    <span className="diff-line-number" role="cell">{row.newLine}</span>
                    <span aria-hidden="true" className="diff-marker">{row.kind === "add" ? "+" : row.kind === "delete" ? "−" : ""}</span>
                  </span>
                  <span className="diff-code" role="cell"><CodeLine commentMode={codeCommentMode} tokens={syntaxByRow.get(row.id) ?? [{ text: row.text }]} /></span>
                </div>
                {renderRowExtras([row])}
              </div>
            );
          })}
      </div>
    </div>
  );
}
